import { useState } from "react";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, ReportRow, ReportStats, reportMeta } from "./Frame";
import {
  Pill,
  StatusPill,
  Money,
  SubHeading,
  Tile,
  plural,
  shortDate,
} from "../kit";
import {
  presentReceiptStatus,
  TONE_CLASSES,
  type ReceiptStatus,
  type StatusTone,
} from "@shared/status";
import { cn } from "@/lib/utils";

/**
 * Report — dispatched, but not received.
 *
 * The one that catches supplies that vanished, so it is built around age rather
 * than around the dispatch number. Oldest first, and the age is the first thing
 * on the row: a big day count, red once it passes the same four-day threshold
 * the alert and the Receive screen already use. A dispatch from this morning
 * should read as unremarkable at a glance and a nine-day-old one should not.
 *
 * Two sections, because they are two different problems:
 *
 *   Never confirmed — the goods left and nobody has tapped anything. Nothing is
 *   invoiced yet, because the invoice is raised on receipt.
 *
 *   Confirmed short — the clinic reported and units were missing, damaged or
 *   short. Those units were never billed, which is `lib/invoicing.ts` working
 *   as specified rather than a billing error; the money figure is what Nexora
 *   shipped and never charged for, which is the number worth chasing a courier
 *   over.
 *
 * 390px first: rows in a ruled list, no table, every long name truncated rather
 * than pushing the layout sideways. The rows carry no box of their own — the
 * report is the card, and how late a dispatch is comes from the age badge,
 * which is already the loudest thing on the row.
 */

type NotReceivedData = FunctionReturnType<typeof api.reports.notReceived.data>;
type UnconfirmedRow = NotReceivedData["unconfirmed"][number];

/** The age bands are the status palette wearing different words: overdue is the
 *  same red as "needs ordering", fresh the same gray as "received". */
const BAND_TONE: Record<string, StatusTone> = {
  overdue: "red",
  watch: "amber",
  fresh: "gray",
};

/**
 * The receipt pill, coloured by how long the goods have been missing.
 *
 * `presentReceiptStatus("pending")` is green — correct on the Receive screen,
 * where a parcel that left this morning is genuinely fine. On this report it
 * put a green "In transit" on a dispatch that had been unaccounted for a
 * hundred and nineteen days, in a row that was red everywhere else. The
 * wording still comes from the one presenter, so the label cannot drift; only
 * the tone follows the age band — the same three bands the alert threshold and
 * the Receive screen already use.
 */
function ReceiptAgePill({ status, band }: { status: string; band: string }) {
  const presented = presentReceiptStatus(status as ReceiptStatus);
  // Only the two late bands override the presenter. `fresh` keeps its own tone
  // — a parcel that left this morning is genuinely green — and the `??` that
  // used to sit inside this branch could never fire, because both bands it
  // guarded are keys of `BAND_TONE`.
  const tone: StatusTone =
    band === "overdue" || band === "watch" ? BAND_TONE[band] : presented.tone;
  return (
    <Pill tone={tone} label={presented.label} qualifier={presented.qualifier} />
  );
}

function AgeBadge({ ageDays, band }: { ageDays: number; band: string }) {
  return (
    <div
      className={cn(
        "num flex w-14 shrink-0 flex-col items-center justify-center rounded-md py-1 ring-1 ring-inset",
        TONE_CLASSES[BAND_TONE[band] ?? BAND_TONE.fresh],
      )}
    >
      <span className="text-lg leading-none font-semibold">{ageDays}</span>
      <span className="text-[10px] leading-tight font-medium uppercase">
        {plural(ageDays, "day")}
      </span>
    </div>
  );
}

/**
 * One unconfirmed dispatch, with its lines folded away.
 *
 * Every dispatch used to print every line it carried, and the report grew to
 * fourteen hundred pixels and swallowed the page. The summary line above the
 * fold already says how many lines, how many units and what they are worth —
 * which is the whole answer for all but the one dispatch someone is actually
 * chasing. That one expands.
 */
function UnconfirmedCard({ row }: { row: UnconfirmedRow }) {
  const [open, setOpen] = useState(false);

  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3">
        <AgeBadge ageDays={row.ageDays} band={row.ageBand} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">
                {row.clinicName}
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {row.number} · sent {shortDate(row.dispatchedAt)} by{" "}
                {row.deliveredBy}
              </div>
            </div>
            <ReceiptAgePill status={row.receiptStatus} band={row.ageBand} />
          </div>

          <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
            <span>
              {row.lineCount} {plural(row.lineCount, "line")} ·{" "}
              {row.unitsOutstanding} {plural(row.unitsOutstanding, "unit")} ·{" "}
              <Money cents={row.valueCents} /> once received
            </span>
            {/* Deliberately raw: an inline trigger sitting on the baseline of
                the `text-xs` line beside it. It sets no size of its own on
                purpose, so the registry base's `text-sm` would put it a step
                above the sentence it belongs to, and `inline-flex` would give
                it a flex baseline instead of a text one — which is what
                `items-baseline` on the row above is aligning to. Teal because
                teal is interaction, and it arrives as `--primary`. */}
            <button
              type="button"
              onClick={() => setOpen((previous) => !previous)}
              aria-expanded={open}
              className="rounded-sm font-medium text-primary transition-colors hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {open ? "Hide items" : "Show items"}
            </button>
          </div>

          {open ? (
            <ul className="mt-2 space-y-0.5">
              {row.items.map((line, index) => (
                <li
                  key={`${row.dispatchId}-${index}`}
                  className="flex gap-2 text-xs"
                >
                  <span className="num font-medium">×{line.qtyDispatched}</span>
                  <span className="min-w-0 flex-1 truncate">
                    {line.itemName}
                  </span>
                </li>
              ))}
              {row.moreItems > 0 ? (
                <li className="text-xs text-muted-foreground">
                  + {row.moreItems} more
                </li>
              ) : null}
            </ul>
          ) : null}

          {row.alerted ? (
            <p className="mt-2 text-xs font-medium text-red-800">
              Alert raised — still not acknowledged
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function UnconfirmedGroup({
  heading,
  rows,
}: {
  heading: string;
  rows: UnconfirmedRow[];
}) {
  return (
    <div>
      <SubHeading as="h4" className="mb-1">{heading}</SubHeading>
      <ul className="divide-y">
        {rows.map((row) => (
          <UnconfirmedCard key={row.dispatchId} row={row} />
        ))}
      </ul>
    </div>
  );
}

export function NotReceived() {
  const data = useQuery(api.reports.notReceived.data);

  const count = data
    ? data.totals.unconfirmedDispatches + data.totals.shortfallDispatches
    : 0;

  /* A dispatch that left today has not been "never confirmed" — it has been
     out for a few hours. Split on the server's own age, which is whole days. */
  const aged = (data?.unconfirmed ?? []).filter((row) => row.ageDays >= 1);
  const sentToday = (data?.unconfirmed ?? []).filter((row) => row.ageDays < 1);

  return (
    <ReportFrame
      {...reportMeta("not-received")}
      question="What left the warehouse and never landed?"
      data={data}
      count={count}
      empty={{
        title: "Every dispatch has been confirmed received",
        body: "Nothing is in transit unconfirmed, and no delivery has reported a missing or damaged line.",
        good: true,
      }}
      footnote={
        data
          ? `Unconfirmed after ${data.alertAfterDays} days is flagged red — the same threshold that raises the alert. Values are at today's pricing rules; an unconfirmed delivery is not invoiced until the clinic confirms it.`
          : null
      }
    >
      {data ? (
        <div className="space-y-4">
          {/* The headline, before any row: how bad, and how old. */}
          <ReportStats>
            <Tile
              size="sm"
              label="Awaiting confirmation"
              value={data.totals.unconfirmedDispatches}
              hint={
                <>
                  {data.totals.unconfirmedUnits}{" "}
                  {plural(data.totals.unconfirmedUnits, "unit")} ·{" "}
                  <Money cents={data.totals.unconfirmedValueCents} />
                </>
              }
            />
            <Tile
              size="sm"
              label={`Over ${data.alertAfterDays} days out`}
              value={data.totals.overdueDispatches}
              tone={data.totals.overdueDispatches > 0 ? "red" : "neutral"}
              hint={
                <>
                  oldest {data.totals.oldestAgeDays}{" "}
                  {plural(data.totals.oldestAgeDays, "day")}
                </>
              }
            />
          </ReportStats>

          {/* ---- Not yet confirmed ---------------------------------------- */}
          {/* "Never confirmed" is a judgement, and it is the wrong one about a
              parcel that left this morning. The heading is gated on age: only
              dispatches that have had at least a day to arrive sit under it. */}
          {aged.length > 0 ? (
            <UnconfirmedGroup heading="Never confirmed" rows={aged} />
          ) : null}
          {sentToday.length > 0 ? (
            <UnconfirmedGroup heading="Sent today" rows={sentToday} />
          ) : null}
          {data.totals.unconfirmedDispatches > data.unconfirmed.length ? (
            <p className="text-xs text-muted-foreground">
              Showing the {data.unconfirmed.length} oldest of{" "}
              {data.totals.unconfirmedDispatches}.
            </p>
          ) : null}

          {/* ---- Confirmed short ------------------------------------------ */}
          {data.shortfalls.length > 0 ? (
            <div className="border-t pt-4">
              <SubHeading as="h4">Missing or damaged on arrival</SubHeading>
              <p className="mb-2 text-xs text-muted-foreground">
                {data.totals.unaccountedUnits}{" "}
                {plural(data.totals.unaccountedUnits, "unit")} shipped and never
                charged for — <Money cents={data.totals.unbilledValueCents} />
              </p>
              <ul className="divide-y">
                {data.shortfalls.map((row) => (
                  <li key={row.dispatchId} className="py-3 first:pt-0 last:pb-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {row.clinicName}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {row.number} · sent {shortDate(row.dispatchedAt)}
                          {row.receivedAt
                            ? ` · confirmed ${shortDate(row.receivedAt)}`
                            : ""}
                          {row.invoiceNumber ? ` · ${row.invoiceNumber}` : ""}
                        </div>
                      </div>
                      <StatusPill status={row.receiptStatus} kind="receipt" />
                    </div>

                    <div className="mt-2">
                      {row.lines.map((line, index) => (
                        <ReportRow
                          key={`${row.dispatchId}-${index}`}
                          label={line.itemName}
                          detail={
                            <span className="flex items-center gap-1">
                              <StatusPill
                                status={line.lineReceiptStatus}
                                kind="receipt"
                              />
                              {/* A damaged line can be "got 4, billed 0" —
                                  the quantity arrived, the charge did not. */}
                              <span>
                                sent {line.qtyDispatched} · got{" "}
                                {line.qtyReceived ?? 0} · billed{" "}
                                {line.qtyDispatched - line.unaccountedUnits}
                              </span>
                            </span>
                          }
                          tone="red"
                          value={
                            <span className="flex flex-col items-end">
                              <span>−{line.unaccountedUnits}</span>
                              <span className="text-xs font-normal text-muted-foreground">
                                <Money cents={line.unbilledValueCents} />
                              </span>
                            </span>
                          }
                        />
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
              {data.totals.shortfallDispatches > data.shortfalls.length ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Showing the {data.shortfalls.length} oldest of{" "}
                  {data.totals.shortfallDispatches}.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </ReportFrame>
  );
}
